import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";


/*
 * =====================================================
 * EDUNET USER-SPECIFIC RECENT SELECTION
 * =====================================================
 * Uses the logged-in student's identifier when one is
 * available, so one student's recent items do not affect
 * another student's homepage on the same browser.
 */
const getEdunetUserKey=()=>{

  const possibleKeys=[
    "user_id",
    "student_id",
    "logged_user_id",
    "current_user_id",
    "auth_user_id",
    "user",
    "student",
    "logged_user",
    "currentUser",
    "auth_user",
    "student_data"
  ];

  for(const key of possibleKeys){

    try{

      const raw=localStorage.getItem(key);

      if(!raw){
        continue;
      }

      try{

        const parsed=JSON.parse(raw);

        if(parsed && typeof parsed==="object"){

          const id=
            parsed.user_id ??
            parsed.student_id ??
            parsed.id ??
            parsed.userId ??
            parsed.studentId ??
            parsed.email;

          if(id!==undefined && id!==null && String(id).trim()!==""){
            return String(id);
          }

        }

      }catch(error){

        if(String(raw).trim()!==""){
          return String(raw);
        }

      }

    }catch(error){}

  }

  return "guest";
};


function GradeSelection() {
  const navigate = useNavigate();

  const [grades, setGrades] = useState([]);
  const [loading, setLoading] = useState(true);

  // =====================================================
  // TEACHERS
  // =====================================================
  // Temporary flow:
  // Until school filtering is added, all teachers from
  // the teacher-demo API are shown in one Teachers row.
  const [teachers, setTeachers] = useState([]);
  const [teacherLoading, setTeacherLoading] = useState(true);

  // =====================================================
  // SUBJECTS
  // =====================================================
  // Subjects are loaded from the existing subject-by-grade
  // API. No backend/controller changes are required.
  const [subjects, setSubjects] = useState([]);
  const [subjectLoading, setSubjectLoading] = useState(true);

  // Teacher Demo style navbar states
  const [darkMode, setDarkMode] = useState(() => {
    try {
      return localStorage.getItem("teacher_demo_theme") !== "light";
    } catch (error) {
      return true;
    }
  });

  const [searchOpen, setSearchOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeNav, setActiveNav] = useState("Home");
  const [accountName, setAccountName] = useState("Kids");

  /*
   * =====================================================
   * MCQ DATA
   * =====================================================
   * The backend returns:
   *
   *   all=1:
   *   {
   *     latest_mcq: {...},
   *     mcq_data: [...]
   *   }
   *
   * The MCQ row shows all unique completed MCQs.
   * The latest completed MCQ is moved to the first card.
   */
  const [recentMcqSelection,setRecentMcqSelection]=useState({
    gradeId:null,
    subjectId:null,
    teacherId:null
  });

  const [latestMcq,setLatestMcq]=useState(null);
  const [allMcqs,setAllMcqs]=useState([]);
  const [mcqLoading,setMcqLoading]=useState(true);

  // =====================================================
  // ESSAY DATA - FIFTH FLOW
  // =====================================================
  const [recentEssaySelection,setRecentEssaySelection]=useState({
    gradeId:null,
    subjectId:null,
    teacherId:null
  });

  const [latestEssay,setLatestEssay]=useState(null);
  const [allEssays,setAllEssays]=useState([]);
  const [essayLoading,setEssayLoading]=useState(true);


  const API_URL =
    "https://edunet.kmechengineering.com/root-engine1/api/grades";

  const TEACHER_API =
    "https://edunet.kmechengineering.com/root-engine1/api/teacher-demo-data";

  const LATEST_MCQ_API =
    "https://edunet.kmechengineering.com/root-engine1/api/mcq-latest";

  const LATEST_ESSAY_API =
    "https://edunet.kmechengineering.com/root-engine1/api/essay-latest";

  // =====================================================
  // LOAD ALL COMPLETED MCQs + LATEST MCQ
  // =====================================================
  useEffect(() => {

    const loadMcqs = async () => {

      const userId = getEdunetUserKey();

      setMcqLoading(true);

      try {

        /*
         * The backend requires a logged-in user for the
         * completed MCQ list.
         */
        if (!userId || userId === "guest") {
          setLatestMcq(null);
          setAllMcqs([]);
          setRecentMcqSelection({
            gradeId:null,
            subjectId:null,
            teacherId:null
          });
          setMcqLoading(false);
          return;
        }

        const response = await fetch(
          `${LATEST_MCQ_API}?user_id=${encodeURIComponent(
            userId
          )}&all=1`
        );

        if (!response.ok) {
          throw new Error("Failed to load MCQs");
        }

        const data = await response.json();

        /*
         * all=1 response:
         *
         * mcq_data: [...]
         * latest_mcq: {...}
         */
        const mcqList = Array.isArray(data?.mcq_data)
          ? data.mcq_data
          : [];

        const latest = data?.latest_mcq || null;

        /*
         * The backend already returns one row per unique
         * teacher + module + MCQ type. Keep a frontend
         * safety check as well.
         */
        const uniqueMcqs = [];
        const seenMcqs = new Set();

        mcqList.forEach((mcq, index) => {

          if (!mcq || typeof mcq !== "object") {
            return;
          }

          const key =
            mcq.mcq_key ||
            `${mcq.teacher_id || ""}-${mcq.module_id || ""}-${mcq.mcq_type || ""}`;

          if (!seenMcqs.has(String(key))) {
            seenMcqs.add(String(key));
            uniqueMcqs.push(mcq);
          }

        });

        setLatestMcq(latest);

        /*
         * Save the latest completed path so the existing
         * Grades / Teachers / Subjects rows can still move
         * the latest selection to the front.
         */
        if (latest) {

          setRecentMcqSelection({
            gradeId:latest.grade_id
              ? String(latest.grade_id)
              : null,

            teacherId:latest.teacher_id
              ? String(latest.teacher_id)
              : null,

            subjectId:latest.subject_id
              ? String(latest.subject_id)
              : null
          });

        } else {

          setRecentMcqSelection({
            gradeId:null,
            subjectId:null,
            teacherId:null
          });

        }

        /*
         * Put latest completed MCQ first.
         */
        let orderedMcqs = [...uniqueMcqs];

        if (latest) {

          const latestKey =
            latest.mcq_key ||
            `${latest.teacher_id || ""}-${latest.module_id || ""}-${latest.mcq_type || ""}`;

          const latestIndex = orderedMcqs.findIndex(
            (mcq) => {

              const key =
                mcq.mcq_key ||
                `${mcq.teacher_id || ""}-${mcq.module_id || ""}-${mcq.mcq_type || ""}`;

              return String(key) === String(latestKey);

            }
          );

          if (latestIndex > 0) {

            orderedMcqs = [
              orderedMcqs[latestIndex],
              ...orderedMcqs.filter(
                (_, index) => index !== latestIndex
              )
            ];

          }

        }

        setAllMcqs(orderedMcqs);

      } catch (error) {

        console.error("MCQ API Error:", error);

        setLatestMcq(null);
        setAllMcqs([]);

        setRecentMcqSelection({
          gradeId:null,
          subjectId:null,
          teacherId:null
        });

      } finally {

        setMcqLoading(false);

      }

    };

    loadMcqs();

  }, []);


  // =====================================================
  // LOAD ALL COMPLETED ESSAYS + LATEST ESSAY
  // =====================================================
  useEffect(() => {

    const loadEssays = async () => {

      const userId = getEdunetUserKey();

      setEssayLoading(true);

      try {

        if (!userId || userId === "guest") {
          setLatestEssay(null);
          setAllEssays([]);
          setRecentEssaySelection({
            gradeId:null,
            subjectId:null,
            teacherId:null
          });
          setEssayLoading(false);
          return;
        }

        const response = await fetch(
          `${LATEST_ESSAY_API}?user_id=${encodeURIComponent(
            userId
          )}&all=1`
        );

        if (!response.ok) {
          throw new Error("Failed to load Essays");
        }

        const data = await response.json();

        const essayList = Array.isArray(data?.essay_data)
          ? data.essay_data
          : [];

        const latest = data?.latest_essay || null;

        const uniqueEssays = [];
        const seenEssays = new Set();

        essayList.forEach((essay) => {

          if (!essay || typeof essay !== "object") {
            return;
          }

          const key =
            essay.essay_key ||
            `${essay.teacher_id || ""}-${essay.module_id || ""}-${essay.essay_type || ""}`;

          if (!seenEssays.has(String(key))) {
            seenEssays.add(String(key));
            uniqueEssays.push(essay);
          }

        });

        setLatestEssay(latest);

        if (latest) {

          setRecentEssaySelection({
            gradeId:latest.grade_id
              ? String(latest.grade_id)
              : null,
            teacherId:latest.teacher_id
              ? String(latest.teacher_id)
              : null,
            subjectId:latest.subject_id
              ? String(latest.subject_id)
              : null
          });

        } else {

          setRecentEssaySelection({
            gradeId:null,
            subjectId:null,
            teacherId:null
          });

        }

        let orderedEssays = [...uniqueEssays];

        if (latest) {

          const latestKey =
            latest.essay_key ||
            `${latest.teacher_id || ""}-${latest.module_id || ""}-${latest.essay_type || ""}`;

          const latestIndex = orderedEssays.findIndex(
            (essay) => {

              const key =
                essay.essay_key ||
                `${essay.teacher_id || ""}-${essay.module_id || ""}-${essay.essay_type || ""}`;

              return String(key) === String(latestKey);
            }
          );

          if (latestIndex > 0) {

            orderedEssays = [
              orderedEssays[latestIndex],
              ...orderedEssays.filter(
                (_, index) => index !== latestIndex
              )
            ];

          }

        }

        setAllEssays(orderedEssays);

      } catch (error) {

        console.error("Essay API Error:", error);

        setLatestEssay(null);
        setAllEssays([]);

        setRecentEssaySelection({
          gradeId:null,
          subjectId:null,
          teacherId:null
        });

      } finally {

        setEssayLoading(false);

      }

    };

    loadEssays();

  }, []);

  useEffect(() => {
    const loadGrades = async () => {
      try {
        const response = await fetch(API_URL);

        if (!response.ok) {
          throw new Error("Failed to load grades");
        }

        const data = await response.json();

        setGrades(data.grade_data || []);
      } catch (error) {
        console.error("Grade API Error:", error);
        setGrades([]);
      } finally {
        setLoading(false);
      }
    };

    loadGrades();
  }, []);

  // =====================================================
  // LOAD ALL SUBJECTS
  // =====================================================
  // Existing API:
  // /api/subject-by-grade?grade_id=...
  //
  // We call the existing endpoint for each loaded grade,
  // then combine and remove duplicate subjects.
  useEffect(() => {
    const loadAllSubjects = async () => {
      if (!grades.length) {
        setSubjects([]);
        setSubjectLoading(false);
        return;
      }

      setSubjectLoading(true);

      try {
        const responses = await Promise.all(
          grades.map(async (grade) => {
            const gradeId = getGradeId(grade);

            if (!gradeId) {
              return [];
            }

            const response = await fetch(
              `https://edunet.kmechengineering.com/root-engine1/api/subject-by-grade?grade_id=${encodeURIComponent(
                gradeId
              )}`
            );

            if (!response.ok) {
              throw new Error(
                `Failed to load subjects for grade ${gradeId}`
              );
            }

            const data = await response.json();

            return (data.subject_data || []).map((subject) => ({
              ...subject,
              grade_id: gradeId,
              grade_name:
                data.grade_name || getGradeName(grade)
            }));
          })
        );

        const allSubjects = responses.flat();

        // Group the same subject together and keep
        // all grades where that subject is available.
        const subjectMap = new Map();

        allSubjects.forEach((subject) => {
          const subjectId =
            subject.subject_id ??
            subject.id ??
            null;

          const rawName =
            subject.name ||
            subject.subject_name ||
            subject.title ||
            "";

          const subjectKey = subjectId !== null
            ? String(subjectId)
            : normalizeSearch(rawName);

          if (!subjectMap.has(subjectKey)) {
            subjectMap.set(subjectKey, {
              ...subject,
              display_name: rawName,
              grades: []
            });
          }

          const currentSubject = subjectMap.get(subjectKey);

          const alreadyHasGrade = currentSubject.grades.some(
            (item) =>
              String(item.grade_id) ===
              String(subject.grade_id)
          );

          if (!alreadyHasGrade) {
            currentSubject.grades.push({
              grade_id: subject.grade_id,
              grade_name: subject.grade_name
            });
          }
        });

        setSubjects(Array.from(subjectMap.values()));
      } catch (error) {
        console.error("Subject API Error:", error);
        setSubjects([]);
      } finally {
        setSubjectLoading(false);
      }
    };

    loadAllSubjects();
  }, [grades]);

  // =====================================================
  // LOAD ALL TEACHERS
  // =====================================================
  useEffect(() => {
    const loadTeachers = async () => {
      setTeacherLoading(true);

      try {
        const response = await fetch(TEACHER_API);

        if (!response.ok) {
          throw new Error("Failed to load teachers");
        }

        const data = await response.json();

        /*
         * teacherDemo API returns:
         * teacher_data: {
         *   primary: [...],
         *   middle: [...],
         *   al: [...]
         * }
         */
        const primary = Array.isArray(data?.teacher_data?.primary)
          ? data.teacher_data.primary
          : [];

        const middle = Array.isArray(data?.teacher_data?.middle)
          ? data.teacher_data.middle
          : [];

        const al = Array.isArray(data?.teacher_data?.al)
          ? data.teacher_data.al
          : [];

        // Combine all categories.
        const allTeachers = [
          ...primary,
          ...middle,
          ...al
        ];

        // A teacher can appear for more than one subject.
        // Show one card per teacher on this temporary page.
        const uniqueTeachers = [];
        const seenTeacherIds = new Set();

        allTeachers.forEach((teacher) => {
          const teacherId =
            teacher.teacher_id ??
            teacher.id ??
            null;

          const key =
            teacherId !== null
              ? String(teacherId)
              : `${teacher.name || ""}-${uniqueTeachers.length}`;

          if (!seenTeacherIds.has(key)) {
            seenTeacherIds.add(key);
            uniqueTeachers.push(teacher);
          }
        });

        setTeachers(uniqueTeachers);
      } catch (error) {
        console.error("Teacher API Error:", error);
        setTeachers([]);
      } finally {
        setTeacherLoading(false);
      }
    };

    loadTeachers();
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(
        "teacher_demo_theme",
        darkMode ? "dark" : "light"
      );
    } catch (error) {}

    document.body.style.backgroundColor = darkMode
      ? "#141414"
      : "#ffffff";

    document.documentElement.style.backgroundColor = darkMode
      ? "#141414"
      : "#ffffff";

    return () => {
      document.body.style.backgroundColor = "";
      document.documentElement.style.backgroundColor = "";
    };
  }, [darkMode]);

  const getGradeName = (grade) => {
    return (
      grade.name ||
      grade.grade_name ||
      grade.grade ||
      grade.title ||
      `Grade ${grade.id}`
    );
  };

  const getGradeId = (grade) => {
    return (
      grade.grade_id ??
      grade.gradeId ??
      grade.id ??
      null
    );
  };

  // =====================================================
  // MCQ GRADE NAME
  // =====================================================
  // The MCQ API returns grade_id. Convert that ID to the
  // same display name used by the Grades row.
  const getMcqGradeName = (mcq) => {

    const gradeId =
      mcq?.grade_id ??
      mcq?.gradeId ??
      null;

    if (!gradeId) {
      return "Grade";
    }

    const matchedGrade = grades.find(
      (grade) =>
        String(getGradeId(grade)) === String(gradeId)
    );

    if (matchedGrade) {
      return getGradeName(matchedGrade);
    }

    // Fallback if the grade is not yet available in the
    // Grades API response.
    if (mcq?.grade_name) {
      return mcq.grade_name;
    }

    return String(gradeId);
  };


  const getEssayGradeName = (essay) => {

    const gradeId =
      essay?.grade_id ??
      essay?.gradeId ??
      null;

    if (!gradeId) {
      return "Grade";
    }

    const matchedGrade = grades.find(
      (grade) =>
        String(getGradeId(grade)) === String(gradeId)
    );

    if (matchedGrade) {
      return getGradeName(matchedGrade);
    }

    if (essay?.grade_name) {
      return essay.grade_name;
    }

    return String(gradeId);
  };

  const getTeacherName = (teacher, index) => {
    return (
      teacher.name ||
      teacher.teacher_name ||
      teacher.full_name ||
      `Teacher ${index + 1}`
    );
  };

  const getTeacherId = (teacher, index) => {
    return (
      teacher.teacher_id ??
      teacher.id ??
      `temp-teacher-${index + 1}`
    );
  };

  const normalizeSearch = (value) => {
    return String(value || "")
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  };

  const search = normalizeSearch(searchText);

  const moveItemToFront=(items,id,getId)=>{

    if(!id){
      return items;
    }

    const index=
      items.findIndex(
        item=>String(getId(item))===String(id)
      );

    if(index<=0){
      return items;
    }

    return[
      items[index],
      ...items.filter(
        (_,itemIndex)=>itemIndex!==index
      )
    ];

  };


  const filteredGradesBase = grades.filter((grade) => {
    if (!search) {
      return true;
    }

    return normalizeSearch(getGradeName(grade)).includes(search);
  });

  const filteredTeachersBase = teachers.filter((teacher, index) => {
    if (!search) {
      return true;
    }

    return normalizeSearch(
      getTeacherName(teacher, index)
    ).includes(search);
  });

  const filteredSubjectsBase = subjects.filter((subject) => {
    if (!search) {
      return true;
    }

    return normalizeSearch(
      subject.display_name ||
      subject.name ||
      subject.subject_name ||
      subject.title ||
      ""
    ).includes(search);
  });

  // Last selected MCQ path item goes to the beginning.
  const filteredGrades=moveItemToFront(
    filteredGradesBase,
    recentMcqSelection.gradeId,
    getGradeId
  );

  const filteredTeachers=moveItemToFront(
    filteredTeachersBase,
    recentMcqSelection.teacherId,
    (teacher,index)=>getTeacherId(teacher,index)
  );

  const filteredSubjects=moveItemToFront(
    filteredSubjectsBase,
    recentMcqSelection.subjectId,
    subject=>(
      subject.subject_id ??
      subject.id ??
      null
    )
  );


  const savePendingMcqSelection=(patch)=>{

    try{

      const userKey=getEdunetUserKey();
      const key=`edunet_pending_mcq_selection_${userKey}`;
      const raw=localStorage.getItem(key);

      let pending={};

      if(raw){
        try{
          pending=JSON.parse(raw) || {};
        }catch(error){
          pending={};
        }
      }

      const next={
        ...pending,
        ...patch,
        updatedAt:new Date().toISOString()
      };

      localStorage.setItem(
        key,
        JSON.stringify(next)
      );

    }catch(error){}

  };

  const handleMcqClick=(mcq)=>{

    if(!mcq){
      return;
    }

    if(!mcq.teacher_id || !mcq.module_id){
      console.error("MCQ teacher_id or module_id is missing:",mcq);
      return;
    }

    navigate(
      `/mcq/free/teacher/${encodeURIComponent(
        mcq.teacher_id
      )}?module_id=${encodeURIComponent(
        mcq.module_id
      )}&mcq_type=${encodeURIComponent(
        mcq.mcq_type || "model_papers"
      )}`
    );
  };


  const handleEssayClick=(essay)=>{

    if(!essay){
      return;
    }

    if(!essay.teacher_id || !essay.module_id){
      console.error(
        "Essay teacher_id or module_id is missing:",
        essay
      );
      return;
    }

    navigate(
      `/essay/free/teacher/${encodeURIComponent(
        essay.teacher_id
      )}?module_id=${encodeURIComponent(
        essay.module_id
      )}&essay_type=${encodeURIComponent(
        essay.essay_type || "model_papers"
      )}`
    );
  };


  const handleNavClick = (item) => {
    setActiveNav(item);
    setMobileMenuOpen(false);

    if (item === "Home") {
      navigate("/teacher-demo");
      return;
    }

    if (item === "Teachers") {
      document
        .getElementById("grade-teachers-row")
        ?.scrollIntoView({
          behavior: "smooth",
          block: "start"
        });
      return;
    }

    if (item === "Subjects") {
      document
        .getElementById("grade-subjects-row")
        ?.scrollIntoView({
          behavior: "smooth",
          block: "start"
        });
      return;
    }

    if (item === "My Learning") {
      navigate("/profile");
    }
  };

  const handleTeacherClick = (teacher) => {
    const teacherId = getTeacherId(teacher, 0);

    if (!teacherId) {
      console.error("Teacher ID is missing:", teacher);
      return;
    }

    // Save selected teacher for the second flow.
    localStorage.setItem(
      "edunet_selected_teacher_id",
      String(teacherId)
    );

    // SECOND FLOW:
    // Teacher -> Teacher Grades -> Subject -> MCQ
    navigate(`/teacher-grades/${encodeURIComponent(teacherId)}`);
  };

  return (
    <div
      className={`grade-selection-page ${
        darkMode ? "dark-mode" : "light-mode"
      }`}
    >
      <style>{`
        * {
          box-sizing: border-box;
        }

        html,
        body {
          margin: 0;
          padding: 0;
          background: #141414;
        }

        body {
          overflow-x: hidden;
        }

        .grade-selection-page {
          min-height: 100vh;
          padding: 0 48px 70px;
          background: #141414;
          color: #fff;
          overflow-x: hidden;
          font-family: Arial, Helvetica, sans-serif;
        }

        /* =================================================
           NAVBAR
        ================================================= */

        .grade-navbar {
          position: sticky;
          top: 0;
          z-index: 5000;
          height: 72px;
          margin: 0 -48px 27px;
          padding: 0 48px;

          display: flex;
          align-items: center;
          justify-content: space-between;

          background: linear-gradient(
            180deg,
            #050505 0%,
            #080808 100%
          );

          border-bottom: 1px solid rgba(255, 255, 255, 0.08);

          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
        }

        .grade-navbar-left {
          display: flex;
          align-items: center;
          gap: 32px;
          min-width: 0;
        }

        .grade-demo-menu {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .grade-demo-menu button {
          border: none;
          background: transparent;
          padding: 10px 17px;
          color: rgba(255, 255, 255, 0.68);
          font-size: 16px;
          line-height: 20px;
          font-weight: 500;
          cursor: pointer;
          transition: background 0.2s ease, color 0.2s ease;
          border-radius: 24px;
          white-space: nowrap;
        }

        .grade-demo-menu button:hover {
          background: rgba(255, 255, 255, 0.08);
          color: #fff;
        }

        .grade-demo-menu button.active {
          background: #333338;
          color: #fff;
          font-weight: 700;
        }

        /* =================================================
           NAVBAR RIGHT
        ================================================= */

        .grade-navbar-right {
          display: flex;
          align-items: center;
          gap: 11px;
        }

        .grade-mobile-menu-button {
          display: none;
          width: 38px;
          height: 38px;
          border: none;
          border-radius: 50%;
          background: transparent;
          color: #fff;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          padding: 0;
          flex-shrink: 0;
        }

        .grade-mobile-menu-button:hover,
        .grade-mobile-menu-button.active {
          background: #333338;
        }

        .grade-mobile-menu-button svg {
          width: 23px;
          height: 23px;
          display: block;
        }

        .grade-theme-toggle {
          width: 38px;
          height: 38px;
          border: none;
          border-radius: 50%;
          background: transparent;
          color: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          padding: 0;
          transition: background 0.2s ease, transform 0.2s ease;
          flex-shrink: 0;
        }

        .grade-theme-toggle:hover {
          background: #333338;
          transform: rotate(8deg);
        }

        .grade-theme-toggle svg {
          width: 21px;
          height: 21px;
          display: block;
          stroke: currentColor;
          stroke-width: 1.8;
          stroke-linecap: round;
          stroke-linejoin: round;
        }

        .grade-search-area {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .grade-search-input {
          width: 220px;
          height: 36px;
          border: 1px solid rgba(255, 255, 255, 0.25);
          border-radius: 4px;
          outline: none;
          background: #181818;
          color: #fff;
          padding: 0 11px;
          font-size: 14px;
        }

        .grade-search-input::placeholder {
          color: rgba(255, 255, 255, 0.5);
        }

        .grade-nav-icon {
          width: 38px;
          height: 38px;
          border: none;
          background: transparent;
          color: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          border-radius: 50%;
          padding: 0;
          flex-shrink: 0;
        }

        .grade-nav-icon:hover,
        .grade-nav-icon.active {
          background: #333338;
        }

        .grade-nav-svg {
          width: 24px;
          height: 24px;
          display: block;
        }

        .grade-search-icon {
          width: 25px;
          height: 25px;
        }

        .grade-notification-icon {
          width: 23px;
          height: 23px;
        }

        .grade-kids-item {
          height: 38px;
          min-width: 38px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 7px;
          padding: 0 7px;
          color: #fff;
          background: transparent;
          border: none;
          cursor: pointer;
          border-radius: 6px;
          transition: background 0.2s ease;
          flex-shrink: 0;
        }

        .grade-kids-item:hover {
          background: #333338;
        }

        .grade-kids-logo {
          width: 32px;
          height: 32px;
          border-radius: 4px;
          overflow: hidden;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(
            135deg,
            #8d42b9 0%,
            #d34f9d 48%,
            #f3b33d 48%,
            #f3b33d 100%
          );
          flex-shrink: 0;
        }

        .grade-kids-logo span {
          color: #fff;
          font-size: 9px;
          font-weight: 800;
          background: rgba(220, 60, 170, 0.75);
          padding: 2px 1px;
        }

        .grade-kids-text {
          font-size: 16px;
          font-weight: 500;
          white-space: nowrap;
        }

        .grade-kids-arrow {
          width: 9px;
          height: 9px;
        }

        .grade-profile-icon {
          width: 38px;
          height: 38px;
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #f2c900;
          color: #fff;
          cursor: pointer;
          transition: background 0.2s ease;
          flex-shrink: 0;
        }

        .grade-profile-icon:hover {
          background: #dcb600;
        }

        .grade-profile-svg {
          width: 24px;
          height: 24px;
        }

        .grade-profile-arrow {
          width: 10px;
          height: 10px;
          color: #aaa;
          margin-left: -4px;
        }

        .grade-mobile-menu-panel {
          display: none;
          position: fixed;
          top: 72px;
          left: 12px;
          right: 12px;
          z-index: 4999;
          padding: 8px;
          background: #181818;
          border: 1px solid rgba(255, 255, 255, 0.12);
          border-radius: 8px;
          box-shadow: 0 15px 40px rgba(0, 0, 0, 0.65);
        }

        .grade-mobile-menu-panel button {
          width: 100%;
          height: 44px;
          border: none;
          border-radius: 6px;
          background: transparent;
          color: #ddd;
          text-align: left;
          padding: 0 14px;
          font-size: 15px;
          cursor: pointer;
        }

        .grade-mobile-menu-panel button:hover {
          background: #292929;
          color: #fff;
        }

        .grade-mobile-menu-panel button.active {
          background: #333338;
          color: #fff;
          font-weight: 600;
        }

        /* =================================================
           GRADE CONTENT
        ================================================= */

        .grade-page-content {
          padding: 0;
        }

        .grade-title {
          font-size: 30px;
          font-weight: 700;
          margin: 0 0 8px;
        }

        .grade-subtitle {
          margin: 0 0 25px;
          color: rgba(255, 255, 255, 0.6);
          font-size: 14px;
        }

        .grade-row-wrapper {
          overflow-x: auto;
          scrollbar-width: none;
          padding: 15px 0 25px;
        }

        .grade-row-wrapper::-webkit-scrollbar {
          display: none;
        }

        .grade-row {
          display: flex;
          gap: 14px;
          width: max-content;
        }

        .grade-card {
          width: 250px;
          height: 140px;
          flex: 0 0 250px;
          border-radius: 7px;
          position: relative;
          overflow: hidden;
          cursor: pointer;

          background: linear-gradient(
            135deg,
            #252525,
            #111
          );

          transition:
            transform 0.25s ease,
            box-shadow 0.25s ease;
        }

        .grade-card:hover {
          transform: scale(1.07);
          box-shadow: 0 15px 40px rgba(0, 0, 0, 0.75);
          z-index: 5;
        }

        .grade-card::after {
          content: "";
          position: absolute;
          inset: 0;
          background: linear-gradient(
            180deg,
            transparent 20%,
            rgba(0, 0, 0, 0.85) 100%
          );
          pointer-events: none;
        }

        .grade-card-number {
          position: absolute;
          top: 15px;
          left: 17px;
          z-index: 5;
          font-size: 13px;
          color: rgba(255, 255, 255, 0.65);
        }

        .grade-card-name {
          position: absolute;
          left: 17px;
          bottom: 16px;
          z-index: 5;
          font-size: 21px;
          font-weight: 700;
          color: #fff;
        }

        /* =================================================
           TEACHERS ROW
        ================================================= */

        .grade-teachers-section {
          margin-top: 18px;
          scroll-margin-top: 95px;
        }

        .grade-teachers-title {
          margin: 0 0 12px;
          font-size: 20px;
          font-weight: 700;
          color: #fff;
        }

        .grade-teachers-subtitle {
          margin: 0 0 10px;
          color: rgba(255, 255, 255, 0.55);
          font-size: 13px;
        }

        .grade-teacher-row-wrapper {
          overflow-x: auto;
          scrollbar-width: none;
          padding: 12px 0 30px;
        }

        .grade-teacher-row-wrapper::-webkit-scrollbar {
          display: none;
        }

        .grade-teacher-row {
          display: flex;
          gap: 14px;
          width: max-content;
        }

        .grade-teacher-card {
          width: 250px;
          height: 120px;
          flex: 0 0 250px;
          border-radius: 7px;
          position: relative;
          overflow: hidden;
          cursor: pointer;

          background: linear-gradient(
            135deg,
            #242424,
            #101010
          );

          border: 1px solid rgba(255, 255, 255, 0.06);

          transition:
            transform 0.25s ease,
            box-shadow 0.25s ease;
        }

        .grade-teacher-card:hover {
          transform: scale(1.05);
          box-shadow: 0 15px 35px rgba(0, 0, 0, 0.7);
          z-index: 5;
        }

        .grade-teacher-number {
          position: absolute;
          top: 14px;
          left: 17px;
          font-size: 13px;
          color: rgba(255, 255, 255, 0.55);
        }

        .grade-teacher-name {
          position: absolute;
          left: 17px;
          bottom: 16px;
          font-size: 19px;
          font-weight: 700;
          color: #fff;
        }

        /* =================================================
           SUBJECT CARDS
        ================================================= */

        #grade-subjects-row {
          margin-top: 8px;
        }

        #grade-subjects-row .grade-teacher-card {
          background: linear-gradient(
            135deg,
            #292929,
            #121212
          );
        }

        .grade-teacher-loading {
          color: #999;
          padding: 25px 0;
        }

        .grade-loading {
          min-height: 250px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #aaa;
          font-size: 15px;
        }

        .grade-empty {
          color: #888;
          padding: 50px 0;
        }

        /* =================================================
           MCQ ROW - FOURTH FLOW
        ================================================= */

        #grade-mcq-row {
          margin-top: 8px;
        }

        .grade-mcq-row-wrapper {
          overflow-x: auto;
          scrollbar-width: none;
          padding: 12px 0 30px;
        }

        .grade-mcq-row-wrapper::-webkit-scrollbar {
          display: none;
        }

        .grade-mcq-row {
          display: flex;
          gap: 14px;
          width: max-content;
        }

        .grade-mcq-card {
          width: 250px;
          min-height: 140px;
          flex: 0 0 250px;
          border-radius: 7px;
          position: relative;
          overflow: hidden;
          cursor: pointer;
          padding: 17px;
          background: linear-gradient(135deg, #292929, #121212);
          border: 1px solid rgba(255, 255, 255, 0.06);
          transition: transform 0.25s ease, box-shadow 0.25s ease;
        }

        .grade-mcq-card:hover {
          transform: scale(1.05);
          box-shadow: 0 15px 35px rgba(0, 0, 0, 0.7);
          z-index: 5;
        }

        .grade-mcq-number {
          font-size: 12px;
          color: rgba(255, 255, 255, 0.55);
          margin-bottom: 9px;
        }

        .grade-mcq-title {
          font-size: 18px;
          font-weight: 700;
          color: #fff;
          margin-bottom: 6px;
        }

        .grade-mcq-meta {
          font-size: 12px;
          line-height: 1.45;
          color: rgba(255, 255, 255, 0.62);
        }

        .grade-mcq-action {
          margin-top: 10px;
          font-size: 12px;
          font-weight: 700;
          color: #fff;
        }

        .grade-mcq-card.recent {
          border-color: rgba(255, 255, 255, 0.20);
        }

        .light-mode .grade-mcq-card {
          background: linear-gradient(135deg, #f4f4f4, #dddddd);
          border-color: rgba(0, 0, 0, 0.08);
        }

        .light-mode .grade-mcq-card.recent {
          border-color: rgba(0, 0, 0, 0.20);
        }

        .light-mode .grade-mcq-number,
        .light-mode .grade-mcq-meta {
          color: rgba(0, 0, 0, 0.55);
        }

        .light-mode .grade-mcq-title,
        .light-mode .grade-mcq-action {
          color: #111;
        }

        /* =================================================
           LIGHT MODE
        ================================================= */

        .grade-selection-page.light-mode {
          background: #ffffff;
          color: #111;
        }

        .light-mode .grade-navbar {
          background: linear-gradient(
            180deg,
            #ffffff 0%,
            #ffffff 100%
          );
          border-bottom: 1px solid rgba(0, 0, 0, 0.1);
        }

        .light-mode .grade-demo-menu button {
          color: rgba(0, 0, 0, 0.65);
        }

        .light-mode .grade-demo-menu button:hover {
          background: rgba(0, 0, 0, 0.07);
          color: #111;
        }

        .light-mode .grade-demo-menu button.active {
          background: #e5e5e5;
          color: #111;
        }

        .light-mode .grade-theme-toggle,
        .light-mode .grade-nav-icon,
        .light-mode .grade-kids-item {
          color: #111;
        }

        .light-mode .grade-theme-toggle:hover,
        .light-mode .grade-nav-icon:hover,
        .light-mode .grade-nav-icon.active,
        .light-mode .grade-kids-item:hover {
          background: #e5e5e5;
        }

        .light-mode .grade-mobile-menu-button {
          color: #111;
        }

        .light-mode .grade-mobile-menu-panel {
          background: #ffffff;
          border-color: rgba(0, 0, 0, 0.12);
          box-shadow: 0 15px 40px rgba(0, 0, 0, 0.25);
        }

        .light-mode .grade-mobile-menu-panel button {
          color: #333;
        }

        .light-mode .grade-mobile-menu-panel button:hover {
          background: #eeeeee;
          color: #111;
        }

        .light-mode .grade-mobile-menu-panel button.active {
          background: #e5e5e5;
          color: #111;
        }

        .light-mode .grade-title,
        .light-mode .grade-teachers-title {
          color: #111;
        }

        .light-mode .grade-subtitle,
        .light-mode .grade-teachers-subtitle {
          color: rgba(0, 0, 0, 0.6);
        }

        .light-mode .grade-card,
        .light-mode .grade-teacher-card {
          background: linear-gradient(
            135deg,
            #f4f4f4,
            #dddddd
          );
          border-color: rgba(0, 0, 0, 0.08);
        }

        .light-mode .grade-card::after {
          background: linear-gradient(
            180deg,
            transparent 20%,
            rgba(0, 0, 0, 0.18) 100%
          );
        }

        .light-mode .grade-card-number,
        .light-mode .grade-teacher-number {
          color: rgba(0, 0, 0, 0.55);
        }

        .light-mode .grade-card-name,
        .light-mode .grade-teacher-name {
          color: #111;
        }

        /* =================================================
           RESPONSIVE
        ================================================= */

        @media (max-width: 900px) {
          .grade-demo-menu {
            gap: 2px;
          }

          .grade-demo-menu button {
            padding: 10px 10px;
            font-size: 14px;
          }

          .grade-navbar {
            padding: 0 25px;
            margin-left: -25px;
            margin-right: -25px;
          }

          .grade-selection-page {
            padding-left: 25px;
            padding-right: 25px;
          }
        }

        @media (max-width: 768px) {
          .grade-selection-page {
            padding: 0 20px 50px;
          }

          .grade-navbar {
            height: 72px;
            margin: 0 -20px 25px;
            padding: 0 20px;
          }

          .grade-demo-menu {
            display: none;
          }

          .grade-mobile-menu-button {
            display: flex;
          }

          .grade-navbar-right {
            gap: 6px;
          }

          .grade-kids-text {
            display: none;
          }

          .grade-kids-item {
            padding: 0 3px;
          }

          .grade-profile-arrow {
            display: none;
          }

          .grade-search-input {
            position: absolute;
            right: 105px;
            top: 18px;
            width: 170px;
          }

          .grade-title {
            font-size: 25px;
          }

          .grade-card {
            width: 210px;
            height: 120px;
            flex-basis: 210px;
          }

          .grade-card-name {
            font-size: 18px;
          }

          .grade-teacher-card {
            width: 210px;
            height: 110px;
            flex-basis: 210px;
          }

          .grade-teacher-name {
            font-size: 17px;
          }

          .grade-mcq-card {
            width: 210px;
            flex-basis: 210px;
          }

          .grade-mobile-menu-panel {
            display: block;
          }
        }

        @media (min-width: 769px) {
          .grade-demo-menu {
            display: flex;
          }

          .grade-navbar-right {
            display: flex;
          }

          .grade-mobile-menu-button {
            display: none;
          }

          .grade-mobile-menu-panel {
            display: none;
          }
        }
      `}</style>

      {/* =====================================================
          NAVBAR
      ===================================================== */}

      <nav className="grade-navbar">
        <div className="grade-navbar-left">
          <div className="grade-demo-menu">
            {[
              "Home",
              "Teachers",
              "Subjects",
              "My Learning"
            ].map((item) => (
              <button
                key={item}
                type="button"
                className={activeNav === item ? "active" : ""}
                onClick={() => handleNavClick(item)}
              >
                {item}
              </button>
            ))}
          </div>
        </div>

        <div className="grade-navbar-right">
          <button
            className={`grade-mobile-menu-button ${
              mobileMenuOpen ? "active" : ""
            }`}
            type="button"
            title="Menu"
            aria-label="Open menu"
            onClick={() => {
              setMobileMenuOpen((current) => !current);
              setSearchOpen(false);
              setSearchText("");
            }}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="4" y1="6" x2="20" y2="6" />
              <line x1="4" y1="12" x2="20" y2="12" />
              <line x1="4" y1="18" x2="20" y2="18" />
            </svg>
          </button>

          <button
            className="grade-theme-toggle"
            type="button"
            title={darkMode ? "Light Mode" : "Dark Mode"}
            aria-label={
              darkMode
                ? "Switch to light mode"
                : "Switch to dark mode"
            }
            onClick={() =>
              setDarkMode((current) => !current)
            }
          >
            {darkMode ? (
              <svg viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="4" />
                <path d="M12 2v2" />
                <path d="M12 20v2" />
                <path d="m4.93 4.93 1.41 1.41" />
                <path d="m17.66 17.66 1.41 1.41" />
                <path d="M2 12h2" />
                <path d="M20 12h2" />
                <path d="m4.93 19.07 1.41-1.41" />
                <path d="m17.66 6.34 1.41-1.41" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="none">
                <path d="M21 12.79 A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79Z" />
              </svg>
            )}
          </button>

          <div className="grade-search-area">
            {searchOpen && (
              <input
                type="text"
                className="grade-search-input"
                placeholder="Search..."
                value={searchText}
                onChange={(event) =>
                  setSearchText(event.target.value)
                }
                autoFocus
              />
            )}

            <button
              className={`grade-nav-icon ${
                searchOpen ? "active" : ""
              }`}
              type="button"
              title="Search"
              onClick={() => {
                setSearchOpen((current) => !current);
                setMobileMenuOpen(false);

                if (searchOpen) {
                  setSearchText("");
                }
              }}
            >
              <svg
                className="grade-nav-svg grade-search-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="7" />
                <line x1="16.5" y1="16.5" x2="21" y2="21" />
              </svg>
            </button>
          </div>

          <button
            className="grade-nav-icon"
            type="button"
            title="Notifications"
          >
            <svg
              className="grade-nav-svg grade-notification-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M18 8 a6 6 0 0 0-12 0 c0 7-3 7-3 9 h18 c0-2-3-2-3-9" />
              <path d="M10 21h4" />
            </svg>
          </button>

          <button
            className="grade-kids-item"
            type="button"
            title="Switch account"
            onClick={() => setAccountName("Kids")}
          >
            <span className="grade-kids-logo">
              <span>kids</span>
            </span>

            <span className="grade-kids-text">
              {accountName}
            </span>

            <svg
              className="grade-kids-arrow"
              viewBox="0 0 10 10"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path d="M2 3.5 5 6.5 8 3.5" />
            </svg>
          </button>

          <div
            className="grade-profile-icon"
            onClick={() => navigate("/profile")}
            title="Profile"
            role="button"
            tabIndex="0"
          >
            <svg
              className="grade-profile-svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="8" cy="8" r="1.2" fill="currentColor" />
              <circle cx="16" cy="8" r="1.2" fill="currentColor" />
              <path d="M7 14 c2.2 2 7.8 2 10 0" />
            </svg>
          </div>

          <svg
            className="grade-profile-arrow"
            viewBox="0 0 10 10"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <path d="M2 3.5 5 6.5 8 3.5" />
          </svg>
        </div>
      </nav>

      {mobileMenuOpen && (
        <div className="grade-mobile-menu-panel">
          {[
            "Home",
            "Teachers",
            "Subjects",
            "My Learning"
          ].map((item) => (
            <button
              key={item}
              type="button"
              className={
                activeNav === item ? "active" : ""
              }
              onClick={() => handleNavClick(item)}
            >
              {item}
            </button>
          ))}
        </div>
      )}

      {/* =====================================================
          GRADE CONTENT
      ===================================================== */}

      <main className="grade-page-content">
        <h1 className="grade-title">
          Grades
        </h1>

        <p className="grade-subtitle">
          Select a grade to continue learning
        </p>

        {loading ? (
          <div className="grade-loading">
            Loading grades...
          </div>
        ) : filteredGrades.length === 0 ? (
          <div className="grade-empty">
            {search
              ? "No matching grades found."
              : "No grades available."}
          </div>
        ) : (
          <div className="grade-row-wrapper">
            <div className="grade-row">
              {filteredGrades.map((grade, index) => {
                const gradeName = getGradeName(grade);
                const gradeId = getGradeId(grade);

                return (
                  <div
                    key={
                      gradeId ||
                      index
                    }
                    className="grade-card"
                    onClick={() => {
                      if (!gradeId) {
                        console.error(
                          "Grade ID is missing:",
                          grade
                        );
                        return;
                      }

                      navigate(
                        `/subject-selection/${encodeURIComponent(
                          gradeId
                        )}`
                      );
                    }}
                  >
                    <div className="grade-card-number">
                      {String(index + 1).padStart(2, "0")}
                    </div>

                    <div className="grade-card-name">
                      {gradeName}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* =====================================================
            TEACHERS ROW - SECOND FLOW START
        ===================================================== */}

        <section
          id="grade-teachers-row"
          className="grade-teachers-section"
        >
          <h2 className="grade-teachers-title">
            Teachers
          </h2>

          <p className="grade-teachers-subtitle">
            Teachers currently available
          </p>

          {teacherLoading ? (
            <div className="grade-teacher-loading">
              Loading teachers...
            </div>
          ) : filteredTeachers.length === 0 ? (
            <div className="grade-empty">
              No teachers available.
            </div>
          ) : (
            <div className="grade-teacher-row-wrapper">
              <div className="grade-teacher-row">
                {filteredTeachers.map((teacher, index) => (
                  <div
                    key={getTeacherId(teacher, index)}
                    className="grade-teacher-card"
                    onClick={() =>
                      handleTeacherClick(teacher)
                    }
                  >
                    <div className="grade-teacher-number">
                      {String(index + 1).padStart(2, "0")}
                    </div>

                    <div className="grade-teacher-name">
                      {getTeacherName(teacher, index)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>

        {/* =====================================================
            SUBJECTS ROW - THIRD FLOW START
            Subject -> Grade -> Module -> Teacher -> MCQ
        ===================================================== */}

        <section
          id="grade-subjects-row"
          className="grade-teachers-section"
        >
          <h2 className="grade-teachers-title">
            Subjects
          </h2>

          <p className="grade-teachers-subtitle">
            Subjects currently available
          </p>

          {subjectLoading ? (
            <div className="grade-teacher-loading">
              Loading subjects...
            </div>
          ) : filteredSubjects.length === 0 ? (
            <div className="grade-empty">
              {search
                ? "No matching subjects found."
                : "No subjects available."}
            </div>
          ) : (
            <div className="grade-teacher-row-wrapper">
              <div className="grade-teacher-row">
                {filteredSubjects.map((subject, index) => {
                  const subjectName =
                    subject.display_name ||
                    subject.name ||
                    subject.subject_name ||
                    subject.title ||
                    `Subject ${index + 1}`;

                  const subjectId =
                    subject.subject_id ??
                    subject.id ??
                    null;

                  return (
                    <div
                      key={
                        subjectId ||
                        `${subjectName}-${index}`
                      }
                      className="grade-teacher-card"
                      onClick={() => {
                        const subjectId =
                          subject.subject_id ??
                          subject.id ??
                          null;

                        const subjectName =
                          subject.display_name ||
                          subject.name ||
                          subject.subject_name ||
                          subject.title ||
                          "";

                        if (!subjectId) {
                          console.error(
                            "Subject ID is missing:",
                            subject
                          );
                          return;
                        }

                        // Save selected subject so the existing
                        // selection components can reuse it.
                        localStorage.setItem(
                          "edunet_selected_subject",
                          JSON.stringify({
                            subject_id: subjectId,
                            subject_name: subjectName
                          })
                        );

                        // THIRD FLOW:
                        // Subject -> Grade -> Module -> Teacher -> MCQ
                        //
                        // Reuse the existing SubjectSelection route.
                        // "subject-first" tells the next component
                        // that this is the new subject-first flow.
                        navigate(
                          `/subject-selection/subject-first?flow=subject-first&subject_id=${encodeURIComponent(
                            subjectId
                          )}&subject_name=${encodeURIComponent(
                            subjectName
                          )}`
                        );
                      }}
                    >
                      <div className="grade-teacher-number">
                        {String(index + 1).padStart(2, "0")}
                      </div>

                      <div className="grade-teacher-name">
                        {subjectName}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </section>

        {/* =====================================================
            MCQ ROW - FOURTH FLOW
            Shows all available MCQs.
            The student's latest completed MCQ is placed first
            when it is available.
        ===================================================== */}

        <section
          id="grade-mcq-row"
          className="grade-teachers-section"
        >
          <h2 className="grade-teachers-title">
            MCQ
          </h2>

          <p className="grade-teachers-subtitle">
            {allMcqs.length > 0
              ? `${allMcqs.length} MCQ${allMcqs.length === 1 ? "" : "s"} available`
              : "No completed MCQs yet"}
          </p>

          {mcqLoading ? (
            <div className="grade-empty">
              Loading MCQs...
            </div>
          ) : allMcqs.length === 0 ? (
            <div className="grade-empty">
              No MCQs available.
            </div>
          ) : (
            <div className="grade-mcq-row-wrapper">
              <div className="grade-mcq-row">
                {allMcqs.map((mcq, index) => {

                  const mcqId =
                    mcq.mcq_key ||
                    `${mcq.teacher_id || ""}-${mcq.module_id || ""}-${mcq.mcq_type || ""}-${index}`;

                  const latestKey =
                    latestMcq
                      ? (
                          latestMcq.mcq_key ||
                          `${latestMcq.teacher_id || ""}-${latestMcq.module_id || ""}-${latestMcq.mcq_type || ""}`
                        )
                      : null;

                  const isRecent =
                    latestKey &&
                    String(latestKey) === String(
                      mcq.mcq_key ||
                      `${mcq.teacher_id || ""}-${mcq.module_id || ""}-${mcq.mcq_type || ""}`
                    );

                  return (
                    <div
                      key={String(mcqId)}
                      className={`grade-mcq-card ${
                        isRecent ? "recent" : ""
                      }`}
                      onClick={() => handleMcqClick(mcq)}
                    >
                      <div className="grade-mcq-number">
                        {String(index + 1).padStart(2, "0")}
                      </div>

                      <div className="grade-mcq-title">
                        {mcq.module_name ||
                          mcq.mcq_name ||
                          mcq.title ||
                          "MCQ"}
                      </div>

                      <div className="grade-mcq-meta">
                        {`Grade: ${getMcqGradeName(mcq)}`}

                        {mcq.subject_name
                          ? ` • Subject: ${mcq.subject_name}`
                          : ""}

                        {mcq.teacher_name
                          ? ` • Teacher: ${mcq.teacher_name}`
                          : ""}
                      </div>

                      <div className="grade-mcq-action">
                        {isRecent
                          ? "Continue MCQ →"
                          : "Start MCQ →"}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </section>

        {/* =====================================================
            ESSAY ROW - FIFTH FLOW
            Same idea as the MCQ row.
        ===================================================== */}

        <section
          id="grade-essay-row"
          className="grade-teachers-section"
        >
          <h2 className="grade-teachers-title">
            Essay
          </h2>

          <p className="grade-teachers-subtitle">
            {allEssays.length > 0
              ? `${allEssays.length} Essay${allEssays.length === 1 ? "" : "s"} available`
              : "No completed Essays yet"}
          </p>

          {essayLoading ? (
            <div className="grade-empty">
              Loading Essays...
            </div>
          ) : allEssays.length === 0 ? (
            <div className="grade-empty">
              No completed Essays available.
            </div>
          ) : (
            <div className="grade-mcq-row-wrapper">
              <div className="grade-mcq-row">

                {allEssays.map((essay, index) => {

                  const essayKey =
                    essay.essay_key ||
                    `${essay.teacher_id || ""}-${essay.module_id || ""}-${essay.essay_type || ""}-${index}`;

                  const currentKey =
                    essay.essay_key ||
                    `${essay.teacher_id || ""}-${essay.module_id || ""}-${essay.essay_type || ""}`;

                  const latestKey = latestEssay
                    ? (
                        latestEssay.essay_key ||
                        `${latestEssay.teacher_id || ""}-${latestEssay.module_id || ""}-${latestEssay.essay_type || ""}`
                      )
                    : null;

                  const isRecent =
                    latestKey &&
                    String(latestKey) === String(currentKey);

                  return (
                    <div
                      key={String(essayKey)}
                      className={`grade-mcq-card ${
                        isRecent ? "recent" : ""
                      }`}
                      onClick={() => handleEssayClick(essay)}
                    >

                      <div className="grade-mcq-number">
                        {String(index + 1).padStart(2, "0")}
                      </div>

                      <div className="grade-mcq-title">
                        {essay.module_name ||
                          essay.essay_name ||
                          essay.title ||
                          "Essay"}
                      </div>

                      <div className="grade-mcq-meta">

                        {`Grade: ${getEssayGradeName(essay)}`}

                        {essay.subject_name
                          ? ` • Subject: ${essay.subject_name}`
                          : ""}

                        {essay.teacher_name
                          ? ` • Teacher: ${essay.teacher_name}`
                          : ""}

                      </div>

                      <div className="grade-mcq-action">
                        {isRecent
                          ? "Continue Essay →"
                          : "Start Essay →"}
                      </div>

                    </div>
                  );

                })}

              </div>
            </div>
          )}

        </section>

      </main>
    </div>
  );
}

export default GradeSelection;

